import { useParams, Navigate, useNavigate } from 'react-router-dom';
import { NavOverlay } from '@/components/NavOverlay';
import { useProjectsBySection, useProjectImages } from '@/hooks/useProjects';
import { useResolveUserId } from '@/hooks/useResolveUserId';
import { useTenant } from '@/contexts/TenantContext';
import { sections } from '@/data/galleryData';

export default function SectionListPage() {
  const { sectionSlug, username: paramUsername } = useParams<{ sectionSlug: string; username: string }>();
  const { username: tenantUsername } = useTenant();
  const slug = sectionSlug;
  const navigate = useNavigate();
  const { userId } = useResolveUserId();

  const { projects, loading } = useProjectsBySection(slug || '', true, userId);

  if (!slug) {
    return <Navigate to="/" replace />;
  }

  const section = sections.find((s) => s.slug === slug);

  const navStyle: React.CSSProperties = {
    fontFamily: "'IBM Plex Mono', monospace",
    fontSize: 14,
    fontWeight: 300,
  };

  const basePath = tenantUsername ? '' : `/${paramUsername}`;

  return (
    <>
      <NavOverlay />
      <div className="min-h-screen bg-background overflow-auto pt-24 pb-16 px-8">
        <h1
          className="uppercase tracking-wider mb-12"
          style={{
            fontFamily: "'IBM Plex Mono', monospace",
            fontSize: 16,
            fontWeight: 300,
          }}
        >
          {section?.name || slug}
        </h1>

        {loading ? (
          <p className="text-xs opacity-40">Carregando...</p>
        ) : projects.length === 0 ? (
          <p className="text-xs opacity-40 uppercase">Nenhum projeto publicado nesta seção.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {projects.map((project) => (
              <button
                key={project.id}
                onClick={() => navigate(`${basePath}/galeria/${project.slug}`)}
                className="text-left group"
              >
                <ProjectThumbnail projectId={project.id} />
                <h2 className="uppercase mt-3 nav-hover-bg inline-block" style={navStyle}>
                  {project.name}
                </h2>
                {project.description && (
                  <p className="text-xs mt-1 opacity-50 whitespace-pre-wrap line-clamp-3">
                    {project.description}
                  </p>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function ProjectThumbnail({ projectId }: { projectId: string }) {
  const { pairs } = useProjectImages(projectId);
  const first = pairs[0];

  if (!first) {
    return (
      <div
        className="w-full aspect-[4/3] flex items-center justify-center"
        style={{ backgroundColor: 'hsl(0 0% 0% / 0.03)' }}
      >
        <span className="text-xs opacity-30 uppercase">Sem imagem</span>
      </div>
    );
  }

  const hasLeft = Boolean(first.left && first.left.trim());
  const hasRight = Boolean(first.right && first.right.trim() && first.right !== first.left);

  return (
    <div className="w-full aspect-[4/3] flex overflow-hidden" style={{ backgroundColor: 'hsl(var(--muted))' }}>
      {hasLeft && <img src={first.left} alt="" className="w-1/2 h-full object-cover" />}
      {hasRight && <img src={first.right} alt="" className="w-1/2 h-full object-cover" />}
      {!hasRight && <div className="w-1/2 h-full" />}
      {!hasLeft && <div className="w-1/2 h-full" />}
    </div>
  );
}
